import { LitElement, html, nothing } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { sheet } from '../styles.js';

export class Checkbox extends LitElement {
	static formAssociated = true;
	static styles = [sheet];

	static get properties() {
		return {
			label: { type: String, reflect: true },
			tip: { type: String, reflect: true },
			inline: { type: Boolean, reflect: true },
			indeterminate: { type: Boolean },
			checked: { type: Boolean, reflect: true },
			disabled: { type: Boolean, reflect: true },
			onValue: { type: String, attribute: 'on-value' },
			offValue: { type: String, attribute: 'off-value' }
		}
	}

	#initialChecked = false;
	#initialDisabled = false;
	#tooltip = null;

	constructor() {
		super();
		this.internals = this.attachInternals();
		this.onValue = true;
		this.offValue = false;
		this.label = '';
		this.tip = '';
		this.inline = false;
		this.indeterminate = false;
		this.checked = false;
		this.disabled = false;
	}

	connectedCallback() {
		super.connectedCallback();
		this.#initialChecked = this.hasAttribute('checked');
		this.#initialDisabled = this.hasAttribute('disabled');
		this.value = this.checked ? this.onValue : this.offValue;
		this.internals.setFormValue(this.value);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#tooltip?.dispose();
		this.#tooltip = null;
	}

	firstUpdated() {
		const label = this.renderRoot.querySelector('label');
		if (label && this.tip) {
			this.#tooltip = new bootstrap.Tooltip(label, { container: this.closest('dialog') ?? 'body' });
		}
	}

	async formResetCallback() {
		this.indeterminate = false;
		this.checked = this.#initialChecked;
		this.disabled = this.#initialDisabled;
		await this.updateComplete;
		const input = this.renderRoot.querySelector('input');
		input.indeterminate = this.indeterminate;
		input.checked = this.checked;
		input.dispatchEvent(new Event('change'));
	}

	updated(changedProps) {
		if (changedProps.has('checked') || changedProps.has('indeterminate')) {
			if (this.indeterminate && this.checked) {
				this.checked = false;
			}
			this.value = this.checked ? this.onValue : this.offValue;
			this.internals.setFormValue(this.value);
			const input = this.renderRoot.querySelector('input');
			if (input) {
				input.checked = this.checked;
				input.indeterminate = this.indeterminate;
			}
		}
	}

	render() {
		return html`
			<div class="form-field ${classMap({ 'd-inline-grid': this.inline })}">
				<input
					type="checkbox"
					id="input"
					name=${this.getAttribute('name') || nothing}
					class="check"
					.checked=${this.checked}
					?disabled=${this.disabled}
					@change=${this.#onChange}
				>
				<label for="input">
					${this.label}
					${this.tip ? html`<span class="help-inline ms-1" data-bs-toggle="tooltip" data-bs-original-title="${this.tip}"><i class="icon-fill icon-question"></i></span>` : ''}
				</label>
			</div>
		`;
	}

	#onChange(event) {
		this.indeterminate = false;
		this.checked = event.target.checked;
		this.value = this.checked ? this.onValue : this.offValue;
		this.internals.setFormValue(this.value);
		this.dispatchEvent(new CustomEvent('checked-changed', { detail: event, bubbles: true, composed: true }));
	}
}

customElements.define('u-checkbox', Checkbox);
